import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AnonymityAssurance from './components/AnonymityAssurance';
import AnonymityWarning from './components/AnonymityWarning';
import CharacterCounter from './components/CharacterCounter';
import CommunityGuidelines from './components/CommunityGuidelines';
import FirstTimeAcknowledgement from './components/FirstTimeAcknowledgement';
import SuccessModal from './components/SuccessModal';
import { createPost } from '../../services/postsService';

const CreatePost = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [isAcknowledgementOpen, setIsAcknowledgementOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [savedDraft, setSavedDraft] = useState('');

  const MAX_CHARACTERS = 2000;

  // Check if user has acknowledged before
  useEffect(() => {
    const hasAcknowledged = localStorage.getItem('onlycuts_acknowledged');
    if (!hasAcknowledged) {
      // Will show modal on first post attempt
    }
  }, []);

  const handleContentChange = (e) => {
    const newContent = e?.target?.value;
    if (newContent?.length <= MAX_CHARACTERS) {
      setContent(newContent);
    }
  };

  const handleSaveDraft = () => {
    setIsSaving(true);
    setTimeout(() => {
      setSavedDraft(content);
      setIsSaving(false);
    }, 800);
  };

  const handlePostAttempt = () => {
    if (!content?.trim() || content?.trim()?.length < 50) return;

    const hasAcknowledged = localStorage.getItem('onlycuts_acknowledged');
    if (!hasAcknowledged) {
      setIsAcknowledgementOpen(true);
    } else {
      handlePost();
    }
  };

  const handleAcknowledgementConfirm = () => {
    localStorage.setItem('onlycuts_acknowledged', 'true');
    setIsAcknowledgementOpen(false);
    handlePost();
  };

  const handlePost = () => {
    setIsPosting(true);
    
    createPost(content)?.then(() => {
        setIsPosting(false);
        setIsSuccessOpen(true);
        setContent('');
        setSavedDraft('');
      })?.catch((error) => {
        setIsPosting(false);
        console.error('Failed to create post:', error);
        alert('Failed to create post. Please try again.');
      });
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    setContent('');
  };

  const canPost = content?.trim()?.length >= 50;

  return (
    <>
      <Helmet>
        <title>Share Your Story - OnlyCuts</title>
        <meta name="description" content="Share your emotional struggles anonymously in a safe, supportive community. Your story matters." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-[60px] lg:pt-[60px]">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="max-w-4xl mx-auto">
              <button
                onClick={() => navigate('/main-feed')}
                className="inline-flex items-center gap-2 text-sm md:text-base text-muted-foreground hover:text-foreground transition-therapeutic focus-ring rounded-md mb-6 md:mb-7 lg:mb-8 press-scale"
              >
                <Icon name="ArrowLeft" size={18} strokeWidth={2} />
                <span>Back to Feed</span>
              </button>

              <AnonymityAssurance />

              <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 mb-6 md:mb-7 lg:mb-8">
                <AnonymityWarning />

                <div className="mb-4 md:mb-5 lg:mb-6">
                  <label htmlFor="post-content" className="block text-sm md:text-base font-medium text-foreground mb-3">
                    What's on your mind?
                  </label>
                  <textarea
                    id="post-content"
                    value={content}
                    onChange={handleContentChange}
                    placeholder="Share your feelings, struggles, or story here. This is a judgment-free space where you can express yourself freely and anonymously. Remember to avoid identifying details..."
                    className="w-full min-h-[300px] md:min-h-[400px] lg:min-h-[500px] px-4 py-3 md:px-5 md:py-4 text-sm md:text-base text-foreground placeholder:text-muted-foreground bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none transition-therapeutic"
                    aria-describedby="character-counter"
                  />
                </div>

                <div id="character-counter" className="mb-6 md:mb-7 lg:mb-8">
                  <CharacterCounter current={content?.length} max={MAX_CHARACTERS} />
                </div>

                {savedDraft && (
                  <div className="mb-6 p-3 md:p-4 bg-success/10 border border-success/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="Save" size={16} color="var(--color-success)" strokeWidth={2} />
                      <p className="text-xs md:text-sm text-success">
                        Draft saved successfully
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    loading={isSaving}
                    iconName="Save"
                    iconPosition="left"
                    disabled={!content?.trim()}
                    className="flex-1"
                  >
                    Save Draft
                  </Button>
                  <Button
                    variant="default"
                    onClick={handlePostAttempt}
                    loading={isPosting}
                    iconName="Send"
                    iconPosition="right"
                    disabled={!canPost}
                    className="flex-1"
                  >
                    Post
                  </Button>
                </div>

                {!canPost && content?.trim()?.length > 0 && (
                  <p className="text-xs md:text-sm text-muted-foreground mt-3 text-center">
                    Please write at least 50 characters to share your story
                  </p>
                )}
              </div>

              <CommunityGuidelines />
            </div>
          </div>
        </main>

        <FirstTimeAcknowledgement
          isOpen={isAcknowledgementOpen}
          onCancel={() => setIsAcknowledgementOpen(false)}
          onConfirm={handleAcknowledgementConfirm}
        />

        <SuccessModal
          isOpen={isSuccessOpen}
          onClose={handleSuccessClose}
        />
      </div>
    </>
  );
};

export default CreatePost;